import React, { useState, useMemo, useContext, useEffect } from 'react';
import { TabContent, TabPane, Nav, NavItem, NavLink, Button, Card, CardBody, CardFooter, ButtonGroup } from 'src/components/ui';

import { canExpand, ADDITIONAL_PROPERTY_FLAG } from '@rjsf/utils';
import { useSideChannelSubscription } from '../../../util/useSideChannel';
import { t } from '@lingui/core/macro';

import PropertiesTable from './PropertiesTable';
import ObjectTable from './ObjectTable';
import GroupedTabItem from './footerButtons/GroupedTabItem';
import useTabGroupTree, { TabGroupsJSONSchema, TabLeaf } from './useTabGroupTree';
import { FieldPathId, Registry, UiSchema } from '@rjsf/utils';

import { JSONObject } from '../../../api/types';
import { JSONSchemaObject } from '../../../util/jsonschemaTypes';
import TitleField from './TitleField';
import useFooterButtons from './footerButtons/useFooterButtons';
import getPathFromId from '../../../util/getPathFromId';
import UWEFormContext from '../../../context/UWEFormContext';
import NavigateToFieldDropdown from './navigateToFieldDropdown/NavigateToFieldDropdown';
import InputPrefix from '../../Icons/inputPrefix';
import { setScrollYById } from '../../../util/scrolling';
import Icon from 'src/components/Icon';
import classnames from 'src/util/classnames';

export type ObjectFieldTemplateProps<FormDataType = JSONObject> = {
  registry: Registry;
  description: React.ReactElement;
  fieldPathId: FieldPathId;
  uiSchema: UiSchema;
  disabled: boolean;
  formData: FormDataType;
  onAddProperty: () => void;
  properties: {
    content: React.ReactElement;
    name: string;
    disabled: boolean;
    readonly: boolean;
    hidden: boolean;
  }[];
  readonly: boolean;
  schema: JSONSchemaObject;
  title: string;
};

function DefaultObjectFieldTemplate<FormDataType = JSONObject>(props: ObjectFieldTemplateProps<FormDataType>) {
  const {
    registry,
    description,
    fieldPathId: { $id },
    uiSchema,
    disabled,
    formData,
    onAddProperty,
    properties,
    readonly: propReadOnly,
    schema,
    title,
  } = props;
  const DescriptionField = registry.templates.DescriptionFieldTemplate;
  const formContext = registry?.formContext;

  const {
    'ui:title': uiTitle,
    'ui:asPropertiesTable': asPropertiesTable,
    'ui:asTable': asTable,
    'ui:asTabPane': asTabPane,
    'ui:sectionType': uiSectionType,
    'ui:expandable': expandable,
    'ui:expandedDefault': expandedDefault = true,
    'ui:labelClassName': labelClassName = '',
    'ui:asCard': asCard = false,
    'ui:asGroupedTabs': asGroupedTabs = false,
    'ui:hideTitle': propHideTitle,
    'ui:hideLabel': propHideLabel,
    'ui:hideDescription': hideDescription = false,
    'ui:footerButtons': uiFooterButtons = [],
    'ui:tabContentClassName': tabContentClassName = '',
    'ui:readonly': uiReadOnly = false,
    'ui:showNavigateToField': showNavigateToField = false, // TODO change to false, just for testing
    'ui:prefixSuffixToggle': uiPrefixSuffixToggle = false,
    'ui:stickyTabs': stickyTabs = false,
  } = uiSchema;

  const hideTitle = useMemo(() => {
    if (propHideLabel != null) return propHideLabel;
    return propHideTitle ?? false;
  }, [propHideLabel, propHideTitle]);

  const UweFormContext = useContext(UWEFormContext);
  const { openTabs = {}, setOpenTabs, setAvailableTabs, formFields, showPrefix, showSuffix, setShowPrefix, setShowSuffix } = UweFormContext || {};

  const readonly = useMemo(() => {
    return propReadOnly || uiReadOnly;
  }, [propReadOnly, uiReadOnly]);

  const canBeTable = useMemo(
    () => asTable && schema.type === 'object' && Object.values(schema.properties || {}).every(row => row.type === 'object'),
    [schema, asTable]
  );

  // V2 grid — true when any direct child property carries v2 grid coords.
  // When true we wrap the default properties list in a 12-column CSS grid so
  // each SchemaFieldTemplate can position itself via gridColumn/gridRow styles.
  const isV2Grid = useMemo(
    () => Object.values((schema as any).properties || {}).some((p: any) => p?.[':colStart'] != null || p?.[':colSpan'] != null),
    [schema]
  );

  const canBePropertiesTable = useMemo(
    () =>
      asPropertiesTable &&
      (!schema.additionalProperties || schema.additionalProperties.type !== 'object') &&
      Object.values(schema.properties || {}).every(({ type }) => type !== 'object'),
    [schema, asPropertiesTable]
  );

  const canBeAsTabPanel = useMemo(() => {
    if (!asTabPane && !asGroupedTabs) return false;
    if (!schema || typeof schema !== 'object') return false;
    if (!schema.properties || typeof schema.properties !== 'object') return false;

    return Object.values(schema.properties).some(prop => prop && prop.type === 'object');
  }, [schema, asTabPane, asGroupedTabs]);

  const tabRoot = useTabGroupTree({
    canBeAsTabPanel,
    asGroupedTabs,
    schema: schema as TabGroupsJSONSchema,
    properties,
    fieldPathId: props.fieldPathId,
  });

  const { items: tabGroups, leafMap, leafTabs } = tabRoot;

  const { sideChannel, setFormDataValues } = formContext;
  const [rootFormData, formObject] = useSideChannelSubscription(sideChannel, [0, 1]) || [{}];

  const path = useMemo(() => getPathFromId($id), [$id]);

  const [expanded, setExpanded] = useState(expandedDefault);

  // For settings forms that don't have FormContext wrapper
  const [selectedTabState, setSeletedTabState] = useState<TabLeaf | null>(null);

  const selectedTab = useMemo(() => {
    if (!canBeAsTabPanel) return null;
    if ($id in openTabs) {
      return leafTabs.find(lt => lt.fieldPathId.$id === openTabs[$id]) || null;
    }
    if (selectedTabState) return selectedTabState;
    return null;
  }, [canBeAsTabPanel, openTabs, leafTabs, selectedTabState]);

  const setSelectedTab = (newTab: TabLeaf | null) => {
    if (setOpenTabs) {
      setOpenTabs(prev => ({
        ...prev,
        [$id]: newTab ? newTab.fieldPathId.$id : '',
      }));
      setTimeout(() => {
        setScrollYById($id, 0);
      }, 50);
    } else {
      setSeletedTabState(newTab);
    }
  };

  useEffect(() => {
    if (!canBeAsTabPanel) return;
    if ($id in openTabs) return;
    if (!leafTabs || leafTabs.length === 0) return;
    if (selectedTabState != null) return;
    setSelectedTab(leafTabs[0]);
    if (setAvailableTabs) {
      setAvailableTabs(prev => ({
        ...prev,
        [$id]: leafTabs.map(lt => lt.fieldPathId.$id),
      }));
    }
  }, [openTabs, setSelectedTab, leafTabs, setAvailableTabs, canBeAsTabPanel, selectedTabState]);

  const bindings = useMemo(
    () => ({
      formContext,
      formObject,
      rootFormData,
      currentTabPath: selectedTab ? `${path}.${selectedTab?.fieldName}` : path,
    }),
    [formContext, formObject, rootFormData, selectedTab]
  );

  const functionBinds = useMemo(
    () => ({
      set: setFormDataValues,
    }),
    [setFormDataValues]
  );

  const { hasFooterBtns, footerBtns } = useFooterButtons({
    uiFooterButtons,
    schema,
    canBeAsTabPanel,
    asGroupedTabs,
    tabRoot,
    selectedTab,
    setSelectedTab,
    rootFormData,
    path,
    bindings,
    functionBinds,
  });

  function toggleExpand(e: any) {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(!expanded);
  }

  const navigateToFieldDropdown = useMemo(() => {
    if (!showNavigateToField) return null;
    return (
      <div className="self-end" style={{ width: '300px' }}>
        <NavigateToFieldDropdown />
      </div>
    );
  }, [showNavigateToField, formFields]);

  const prefixSuffixToggle = useMemo(() => {
    if (!uiPrefixSuffixToggle) return null;
    return (
      <div>
        <ButtonGroup>
          <Button
            className="icon-button"
            color="primary"
            size="sm"
            outline
            onClick={() => {
              setShowPrefix((prev: boolean) => !prev);
            }}
            active={showPrefix}
          >
            <InputPrefix className="input-prefix-icon" height={15} width={15} />
          </Button>
          <Button
            color="primary"
            className="icon-button"
            outline
            size="sm"
            onClick={() => {
              setShowSuffix((prev: boolean) => !prev);
            }}
            active={showSuffix}
          >
            <InputPrefix className="flip-h input-prefix-icon" height={15} width={15} />
          </Button>
        </ButtonGroup>
      </div>
    );
  }, [showSuffix, showPrefix, setShowPrefix, setShowSuffix]);

  const fieldsetContent = useMemo(() => {
    if (canBeTable) return <ObjectTable {...(props as any)} />;

    if (canBePropertiesTable) {
      return (
        <PropertiesTable
          properties={properties}
          uiSchema={uiSchema}
          onAddClick={canExpand(schema, uiSchema, formData) ? onAddProperty : null}
          readonly={disabled || readonly}
          registry={registry}
        />
      );
    }

    if (canBeAsTabPanel) {
      return (
        <div className="object-field-tabs full-width">
          {asGroupedTabs ? (
            // Grouped Tabs
            <>
              <Nav tabs className={`small form-grouped-tabs${stickyTabs ? ' sticky-tab-nav' : ''}`}>
                <div className="flex gap-2 justify-start flex-wrap">
                  {tabGroups?.map((tabGroup, index) => {
                    return (
                      <GroupedTabItem
                        key={`grouped-tab-${index}`}
                        tab={tabGroup}
                        index={index}
                        selection={selectedTab}
                        onSelect={setSelectedTab}
                        schema={schema as any}
                        rootFormData={rootFormData}
                        bindings={bindings}
                        toggleClassName="footer-btn-grouped-tab-item"
                      />
                    );
                  })}
                </div>
                <div className="self-end flex items-center gap-2" style={{ width: '300px' }}>
                  {prefixSuffixToggle}
                  {navigateToFieldDropdown}
                </div>
              </Nav>
              <TabContent className={`form-tab-content ${tabContentClassName}`} activeTab={selectedTab?.path.key}>
                {properties.map((pane, index) => (
                  <TabPane tabId={leafMap[pane.name].path.key} key={index}>
                    {pane.content}
                  </TabPane>
                ))}
              </TabContent>
            </>
          ) : (
            // Non-grouped Tabs
            <>
              <Nav tabs className={`form-tabs${stickyTabs ? ' sticky-tab-nav' : ''}`}>
                {properties.map(item => {
                  if (!item.content) return null;
                  const tab = leafMap[item.name];
                  return (
                    <NavItem key={tab.leafIndex} style={{ flexGrow: 1 }}>
                      <NavLink
                        className="small animated-show w-full text-center"
                        active={selectedTab?.path.key === tab.path.key}
                        onClick={() => {
                          setSelectedTab(tab);
                        }}
                      >
                        {tab.title}
                      </NavLink>
                    </NavItem>
                  );
                })}
                {navigateToFieldDropdown}
              </Nav>
              <TabContent className="form-tab-content" activeTab={selectedTab?.path.key}>
                {properties.map((pane, index) => (
                  <TabPane tabId={leafMap[pane.name].path.key} key={index}>
                    {pane.content}
                  </TabPane>
                ))}
              </TabContent>
            </>
          )}
        </div>
      );
    }

    return (
      <>
        {isV2Grid ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '8px' }}>{properties.map(prop => prop.content)}</div>
        ) : (
          properties.map(prop => prop.content)
        )}
        {/* <div style={isV2Grid ? { display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '8px' } : undefined}>
          {properties.map(prop => prop.content)}
        </div> */}
        {canExpand(schema, uiSchema, formData) && (
          <Button color="info" className="object-property-expand" onClick={onAddProperty} disabled={disabled || readonly}>
            <Icon icon="Plus" weight="regular" /> {t`Add`}
          </Button>
        )}
      </>
    );
  }, [
    props,
    canBeTable,
    canBePropertiesTable,
    canBeAsTabPanel,
    asGroupedTabs,
    tabGroups,
    selectedTab,
    rootFormData,
    bindings,
    prefixSuffixToggle,
    navigateToFieldDropdown,
  ]);

  const fieldset = (
    <fieldset id={$id} className={classnames({ 'object-table-fieldset': canBeTable })}>
      {(uiTitle || title) && !hideTitle && (
        <TitleField
          className={labelClassName}
          id={$id}
          isSection
          sectionType={uiSectionType}
          title={uiTitle || title}
          bindings={bindings}
          expandable={expandable}
          expanded={expanded}
          toggleExpand={toggleExpand}
        />
      )}
      {expanded || !expandable ? (
        <>
          {description && !hideDescription && (
            <DescriptionField id={`${$id}__description`} description={description} formContext={formContext} registry={registry} />
          )}
          {fieldsetContent}
        </>
      ) : null}
    </fieldset>
  );

  if (asCard) {
    return (
      <Card className="card-shadow">
        <CardBody>{fieldset}</CardBody>
        {hasFooterBtns && (
          <CardFooter className="object-field-template-footer">
            {footerBtns}
            <div className="object-field-template-footer-spacer" />
          </CardFooter>
        )}
      </Card>
    );
  }

  if (hasFooterBtns) {
    return (
      <div className="flex flex-col">
        {fieldset}
        <div className="object-field-template-footer flex">{footerBtns}</div>
      </div>
    );
  }

  return fieldset;
}

export default DefaultObjectFieldTemplate;
